import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';

export type OnboardingCheckoutTableColors = {
  /** Card background */
  surface: string;
  /** Labels and primary amounts on the light surface */
  primaryText: string;
  /** Discounts and bonuses */
  accent: string;
  /** Footer row background (default: same tint as success `Notice`) */
  footerSurface: string;
  /** Text on the footer bar */
  footerText: string;
  /** Divider between the first and second block */
  divider: string;
};

export type OnboardingCheckoutTableCopy = {
  courseFeeLabel: string;
  courseFeeValue: string;
  insurerRefundLabel: string;
  insurerRefundValue: string;
  physiofitSubsidyLabel: string;
  physiofitSubsidyValue: string;
  yourCostsLabel: string;
  yourCostsValue: string;
  insurerBonusLabel: string;
  insurerBonusValue: string;
  footerLabel: string;
  footerValue: string;
  /** Screen-reader summary for the pricing table */
  a11ySummary: string;
};

const DEFAULT_COLORS: OnboardingCheckoutTableColors = {
  surface: getMediumRGBA(0.05),
  primaryText: colors.dark,
  accent: colors.discount,
  footerSurface: colors.medium,
  footerText: colors.white,
  divider: getMediumRGBA(0.22),
};

const DEFAULT_COPY: OnboardingCheckoutTableCopy = {
  courseFeeLabel: 'Position',
  courseFeeValue: '0 €',
  insurerRefundLabel: 'Abzug',
  insurerRefundValue: '– 0 €',
  physiofitSubsidyLabel: 'Zuschuss',
  physiofitSubsidyValue: '– 0 €',
  yourCostsLabel: 'Summe',
  yourCostsValue: '0 €',
  insurerBonusLabel: 'Bonus',
  insurerBonusValue: '+ 0 €',
  footerLabel: 'Ergebnis',
  footerValue: '0 €',
  a11ySummary: 'Kostenübersicht',
};

const Root = styled.section<{ $surface: string }>`
  border-radius: ${radii.control};
  background: ${({ $surface }) => $surface};
  overflow: hidden;
  padding: ${space.md};
`;

/**
 * Auto layout, not `fixed`: the value column shrinks to its (nowrap) amount and the label takes the
 * rest, so long labels like "Erstattung durch die TK (80 %)" stay on one line as the design draws
 * them. `fixed` split the columns 50/50 and wrapped every label (ERS-61 feedback).
 */
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  tbody tr:not(:last-child) th,
  tbody tr:not(:last-child) td {
    padding-bottom: 5px;
  }
`;

const Caption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const LabelCell = styled.th<{ $color: string }>`
  text-align: left;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 400;
  color: ${({ $color }) => $color};
  vertical-align: top;
`;

const ValueCell = styled.td`
  text-align: right;
  font-family: ${fontFamily.barlow};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  font-weight: 700;
  color: ${colors.dark};
  white-space: nowrap;
  vertical-align: top;
`;

const Foot = styled.tfoot`
  tr:last-child th {
    border-bottom-left-radius: ${radii.control};
  }

  tr:last-child td {
    border-bottom-right-radius: ${radii.control};
  }

  tr:first-child th,
  tr:first-child td {
    border-top: 2px solid ${getMediumRGBA(0.1)};
    padding-top: 10px;
  }
`;

const FooterValue = styled(ValueCell)<{ $footerText: string; $emphasis?: boolean }>`
  font-size: ${({ $emphasis }) => ($emphasis ? '1.25rem' : text.small.fontSize)};
`;

export type OnboardingCheckoutTableProps = HTMLAttributes<HTMLElement> & {
  /** Line items and footer copy; omitted fields use generic placeholders. */
  copy?: Partial<OnboardingCheckoutTableCopy>;
  /** Visual palette; defaults use onboarding palette tokens. */
  colors?: Partial<OnboardingCheckoutTableColors>;
  /** When true, shows the accent footer row (e.g. “Dein Plus”). */
  showFooter?: boolean;
  /** When false, omits the Physiofit subsidy row (e.g. Barmer 100% reimbursement). */
  showPhysiofitSubsidy?: boolean;
  /** Larger footer value (Figma "Gesamt für dich": 20 px against 14 px labels). */
  footerValueEmphasis?: boolean;
  /** Plain second footer row under the emphasised total (Figma "Heute fällig"). */
  secondaryFooter?: { label: string; value: string };
};

function resolveColors(partial?: Partial<OnboardingCheckoutTableColors>): OnboardingCheckoutTableColors {
  return { ...DEFAULT_COLORS, ...partial };
}

function resolveCopy(partial?: Partial<OnboardingCheckoutTableCopy>): OnboardingCheckoutTableCopy {
  return { ...DEFAULT_COPY, ...partial };
}

export function OnboardingCheckoutTable({
  copy: copyProp,
  colors: colorsProp,
  showFooter = false,
  showPhysiofitSubsidy = true,
  footerValueEmphasis = false,
  secondaryFooter,
  className,
  ...rest
}: OnboardingCheckoutTableProps) {
  const palette = resolveColors(colorsProp);
  const copy = resolveCopy(copyProp);

  return (
    <Root {...rest} className={className} $surface={palette.surface}>
      <Table>
        <Caption>{copy.a11ySummary}</Caption>
        <tbody>
          <tr>
            <LabelCell scope="row" $color={palette.primaryText}>
              {copy.courseFeeLabel}
            </LabelCell>
            <ValueCell>{copy.courseFeeValue}</ValueCell>
          </tr>
          <tr>
            <LabelCell scope="row" $color={palette.primaryText}>
              {copy.insurerRefundLabel}
            </LabelCell>
            <ValueCell>{copy.insurerRefundValue}</ValueCell>
          </tr>
          {showPhysiofitSubsidy ? (
            <tr>
              <LabelCell scope="row" $color={palette.primaryText}>
                {copy.physiofitSubsidyLabel}
              </LabelCell>
              <ValueCell>{copy.physiofitSubsidyValue}</ValueCell>
            </tr>
          ) : null}
        </tbody>
        {showFooter ? (
          <Foot>
            <tr>
              <LabelCell scope="row" $color={palette.primaryText}>
                {copy.footerLabel}
              </LabelCell>
              <FooterValue $footerText={palette.footerText} $emphasis={footerValueEmphasis}>
                {copy.footerValue}
              </FooterValue>
            </tr>
            {secondaryFooter ? (
              <tr>
                <LabelCell $color={palette.primaryText}>{secondaryFooter.label}</LabelCell>
                <FooterValue $footerText={palette.footerText}>{secondaryFooter.value}</FooterValue>
              </tr>
            ) : null}
          </Foot>
        ) : null}
      </Table>
    </Root>
  );
}
